// src/services/ActivityService.js - Enhanced with detailed change tracking
import { supabase } from '@/lib/supabase';

export class ActivityService {
  
  // Helper function to calculate specific changes between old and new data
  static calculateChanges(oldData, newData, fieldMappings = {}) {
    if (!oldData || !newData) return null;
    
    const changes = [];
    const ignoredFields = ['id', 'created_at', 'updated_at', 'user_id'];
    
    // Default field mappings for better readability
    const defaultMappings = {
      targa: 'Targa',
      marca: 'Marca', 
      modello: 'Modello',
      numero_telaio: 'Numero Telaio',
      cliente_id: 'Cliente',
      stato_id: 'Stato',
      campata_id: 'Posizione',
      data_ingresso: 'Data Ingresso',
      data_uscita: 'Data Uscita',
      note: 'Note',
      ...fieldMappings
    };
    
    // Compare each field
    Object.keys(newData).forEach(key => {
      if (ignoredFields.includes(key)) return;
      
      const oldValue = oldData[key];
      const newValue = newData[key];
      
      // Skip if values are the same
      if (oldValue === newValue) return;
      
      // Handle null/undefined values
      const oldDisplay = oldValue == null ? '(vuoto)' : String(oldValue);
      const newDisplay = newValue == null ? '(vuoto)' : String(newValue);
      
      // Get human-readable field name
      const fieldName = defaultMappings[key] || key;
      
      changes.push({
        field: key,
        fieldName: fieldName,
        oldValue: oldValue,
        newValue: newValue,
        oldDisplay: oldDisplay,
        newDisplay: newDisplay,
        description: `${fieldName}: "${oldDisplay}" → "${newDisplay}"`
      });
    });
    
    return changes.length > 0 ? changes : null;
  }
  
  // Helper to format changes for human reading
  static formatChangesDescription(changes) {
    if (!changes || changes.length === 0) return null;
    
    if (changes.length === 1) {
      return `Modificato ${changes[0].description}`;
    }
    
    const descriptions = changes.map(change => change.description);
    return `Modificato: ${descriptions.join(', ')}`;
  }
  
  static async logActivity({
    action,
    tableName,
    recordId = null,
    recordIdentifier = null,
    oldData = null,
    newData = null,
    changes = null,
    fieldMappings = {}
  }) {
    try {
      console.log('🔄 Starting activity log:', { action, tableName, recordIdentifier });
      
      // Get current user info
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('❌ Could not get user for activity logging:', userError);
        return false;
      }

      console.log('✅ User found for logging:', user.email);

      // Calculate changes if we have both old and new data
      let calculatedChanges = changes;
      let changeDescription = null;
      
      if (action.toUpperCase() === 'UPDATE' && oldData && newData) {
        console.log('🔍 Calculating changes between old and new data...');
        calculatedChanges = this.calculateChanges(oldData, newData, fieldMappings);
        changeDescription = this.formatChangesDescription(calculatedChanges);
        
        console.log('📋 Calculated changes:', calculatedChanges);
        console.log('📝 Change description:', changeDescription);
      }

      // Get user profile for better user identification
      let userName = user.email;
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();
        
        if (profile && (profile.first_name || profile.last_name)) {
          userName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
        }
      } catch (profileError) {
        console.warn('⚠️ Could not get user profile, using email:', profileError);
      }

      // Enhanced activity log entry
      const activityData = {
        user_id: user.id,
        user_email: user.email,
        user_name: userName,
        action: action.toUpperCase(),
        table_name: tableName,
        record_id: recordId,
        record_identifier: recordIdentifier,
        old_data: oldData,
        new_data: newData,
        changes: calculatedChanges,
        change_description: changeDescription,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        ip_address: null // Could be added if available
      };

      console.log('📝 Inserting activity log:', {
        ...activityData,
        old_data: oldData ? '[Object]' : null,
        new_data: newData ? '[Object]' : null,
        changes: calculatedChanges ? `[${calculatedChanges.length} changes]` : null
      });

      // Insert activity log
      const { data: result, error: insertError } = await supabase
        .from('activity_log')
        .insert(activityData);

      if (insertError) {
        console.error('❌ Error inserting activity log:', insertError);
        return false;
      } else {
        console.log(`✅ Activity logged successfully: ${action} on ${tableName}`, recordIdentifier);
        if (changeDescription) {
          console.log(`📋 Changes: ${changeDescription}`);
        }
        return true;
      }

    } catch (error) {
      console.error('💥 Exception in activity logging:', error);
      return false;
    }
  }

  // Enhanced convenience methods with field mappings support
  static async logCreate(tableName, newData, recordIdentifier, fieldMappings = {}) {
    return this.logActivity({
      action: 'CREATE',
      tableName,
      recordId: newData?.id,
      recordIdentifier,
      newData,
      fieldMappings
    });
  }

  static async logUpdate(tableName, recordId, oldData, newData, recordIdentifier, fieldMappings = {}) {
    return this.logActivity({
      action: 'UPDATE',
      tableName,
      recordId,
      recordIdentifier,
      oldData,
      newData,
      fieldMappings
    });
  }

  static async logArchive(tableName, recordId, oldData, recordIdentifier, fieldMappings = {}) {
    return this.logActivity({
      action: 'ARCHIVE',
      tableName,
      recordId,
      recordIdentifier,
      oldData,
      fieldMappings
    });
  }
  
  // New method for custom actions
  static async logCustom(action, tableName, recordId, recordIdentifier, additionalData = {}) {
    return this.logActivity({
      action,
      tableName,
      recordId,
      recordIdentifier,
      ...additionalData
    });
  }
  
  // Helper method to get recent activities for a specific record
  static async getRecordActivities(tableName, recordId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('activity_log')
        .select('*')
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching record activities:', error);
      return [];
    }
  }
  
  // Helper method to get user activities
  static async getUserActivities(userId, limit = 20) {
    try {
      const { data, error } = await supabase
        .from('activity_log')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user activities:', error);
      return [];
    }
  }
}

export default ActivityService;